import * as React from "react";
import Floor from "../models/Floor";
import BaseDialog, { Field, FieldSet } from "./BaseDialog";
import BaseModelMap from "../models/BaseModelMap";
import { useRef, useState } from "react";
import CabinetSelection from "../components/CabinetSelection";
import Logger from "../Logger";
import Cabinet from "../models/Cabinet";
import BaseModel from "../models/BaseModel";
import getFloorOptionsList from "../components/Quickstart/FloorOptionsList";
import Page from "../models/Page";
import UploadFloorPlansDialog from "../components/UploadFloorPlansDialog";
import { itx } from "../translationItx/itx";
import { showToast } from "../components/Toasts";
import { EventKey } from "../eventLogger/EventKeys";
import { C1_Floor } from "../../ConceptOne/src/model/C1_Floor";
import { C1_PdfImportButton } from "../../ConceptOne/src/components/C1_PdfImportButton";
import type { PdfImportPageSelection } from "../../ConceptOne/src/dialogs/C1_Dialog_PdfImport";

const logger = new Logger();

export default function FloorDialog(props: {
  parent: BaseModelMap<Floor>;
  obj?: Floor;
  fromSubmittals?: {
    tag: string;
    name: string;
    cabinetId: string;
    background: { page: Page | unknown; pageNumber: number };
  };
}) {
  const { parent, fromSubmittals } = props;
  let { obj } = props;

  const [name, setName] = useState(
    obj?.name || fromSubmittals?.name || getNewName(state.translate("components.Floor", "Etage "), state.floors),
  );
  const [tag, setTag] = useState(
    obj?.tag ||
      fromSubmittals?.tag ||
      state.nameAndTagManager.generateTag(new Floor({ isTemporary: true }, parent)),
  );
  const [cabinetId, setCabinetId] = useState(
    obj?.cabinetId || fromSubmittals?.cabinetId || state.cabinets.asArray()[0]?.id || "",
  );
  const [cabinetIsMissing, setCabinetIsMissing] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  const tagIsSetManual = useRef<boolean>(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current?.focus();
      }
    }, 100);
  }, []);

  const edit = obj != undefined;

  const options: any = [];

  const floorOptions = getFloorOptionsList();

  floorOptions.forEach((o) => {
    options.push(<option value={o}>{o}</option>);
  });

  if (!edit && parent == undefined) {
    logger.error("parent == undefined");
  }

  function updateName(newValue: string) {
    setName(newValue);

    if (!tagIsSetManual.current || tag == "") {
      const dummyFloor = new Floor({ isTemporary: true }, state.floors);
      state.floors.addWithoutUpdate(dummyFloor);
      dummyFloor.name = newValue;
      dummyFloor.generateTagFromName();

      const tag = dummyFloor.tag;
      setTag(tag);
      if (tagInputRef.current) {
        tagInputRef.current.value = tag;
      }

      state.floors.removeWithoutUpdate(dummyFloor);
    }
  }

  // check if name is already assingned
  function checkValidName(name: string, otherParent?: BaseModelMap<BaseModel>): boolean {
    var double = false;

    let parentMap = otherParent;
    if (!parentMap) {
      parentMap = parent;
    }
    parentMap.items.forEach((item) => {
      if (state.noDoubleValue.ciEquals(item.name, name)) {
        double = true;
      }
    });

    return !double;
  }

  function getNewTag(tagwithoutNumber: string, newName?: string): string {
    if (newName) {
      return tagwithoutNumber + newName.replace(/[^0-9]/g, "");
    }
    return tagwithoutNumber;
  }
  function getNewName(partName: string, parentMap: BaseModelMap<BaseModel>): string {
    var i = 1;
    var newName = partName + i;
    while (!checkValidName(newName, parentMap)) {
      newName = partName + i;
      i++;
    }

    return newName;
  }
  function ShowBackground(props: {}) {
    const bPage = fromSubmittals?.background.page as Page;
    if (bPage) {
      return (
        <div style={{ alignContent: "center", padding: "5px" }}>
          {bPage.tag} {bPage.name}
        </div>
      );
    } else {
      return null;
    }
  }

  function createNewFloor(floorTag?: string, floorName?: string): boolean {
    //check if tag is doubl
    const listAllFloorsTags = new Array<string>();

    state.floors.forEach((floor) => {
      listAllFloorsTags.push(floor.tag);
    });
    if (listAllFloorsTags.includes(tag)) {
      showToast({
        text: state.translate(
          "floorDialog.tagAlreadyUsed",
          `Ihr eingegebner Tag ist schon an vergeben. Bitte ändern Sie diesen!`,
        ),
      });
      return false;
    }
    // create cabinet if selected
    let xcabinetId = cabinetId;
    if (xcabinetId == "create") {
      let cabinetName = state.translate("components.Cabinet", "Schaltschrank") + " " + name;
      if (state.cabinets.size == 0) {
        cabinetName = "";
      }

      const newCabinet = new Cabinet(
        {
          name: cabinetName,
        },
        state.cabinets,
      );
      newCabinet.tag = state.nameAndTagManager.generateTag(newCabinet);
      state.cabinets.add(newCabinet);
      xcabinetId = newCabinet.id;
      setCabinetId(xcabinetId);
    }

    if (edit) {
      // all this isn`t used anymore
      if (obj) {
        obj.name = name;
        obj.tag = tag;
        obj.cabinetId = xcabinetId;
        obj.raiseOnChange();
        state.selectionManager.selectedObject = obj;
      }
    } else {
      let tagFromInput = floorTag || (document.getElementById("FloorDialog_tag") as HTMLInputElement)?.value;
      if (!tagFromInput) {
        tagFromInput = tag;
      }
      let nameFromInput = floorName || (document.getElementById("FloorDialog_name") as HTMLInputElement)?.value;
      if (!nameFromInput) {
        nameFromInput = name;
      }
      const newFloor = new Floor({ name: nameFromInput, tag: tagFromInput, cabinetId: xcabinetId }, parent);
      if (state.projectMetadata.isC1Project) {
        const levels = state.floors.asArray().flatMap(floor =>
          floor.c1 && Number.isFinite(floor.c1.level) ? [floor.c1.level] : [],
        );
        newFloor.c1 = new C1_Floor({
          level: levels.length ? Math.max(...levels) + 1 : 0,
          ownCabinet: cabinetId === "create",
        }, newFloor);
      }
      parent?.add(newFloor);
      state.selectionManager.selectedObject = newFloor;
      // now open Floor
      newFloor.treeViewModel.handleOpen();
      obj = newFloor;
      state.eventLogger.addEvent(EventKey.ObjectCreated, {
        objectType: "Floor",
        obj: newFloor.id + "_" + newFloor.tag + "_" + newFloor.name,
      });
    }
    // set here the background
    const selectedPagePage = fromSubmittals?.background.page as Page;
    const page = selectedPagePage ? obj?.getOrCreatePage() : undefined;
    if (selectedPagePage && page) {
      const pageNumber = fromSubmittals?.background.pageNumber;
      if (selectedPagePage.className == "PdfPageReference" && pageNumber) {
        const pdfDocument = selectedPagePage.parent as Page;

        page.setBackgroundImageFromBuildingPdf(pdfDocument, pageNumber);
      } else if (page.className == "Page" && page) {
        page.setBackgroundImageFromBuildingPdf(selectedPagePage as Page, 1);
      }
      page.raiseOnChange("pdfPageRef");
    }
    return true;
  }

  function FloorPlansSelection(props: {}) {
    const options = new Array<any>();
    const bPage = fromSubmittals?.background.page as Page;
    if (bPage) {
      options.push(
        <option value={bPage.id}>
          {bPage.tag} {bPage.name}
        </option>,
      );
    } else {
      options.push(
        <option value={""}>{itx("floorDialog.noBackgroudSelected", "noch keinen Hintergrund ausgewählt")}</option>,
      );
    }
    options.push(
      <option value={"selectBuildingPlan"}>{itx("floorDialog.selectBuildingPlan", "Gebäudeplan auswählen")}</option>,
    );

    return (
      <select
        className="select"
        onChange={(ev) => {
          const value = ev.target.value;
          if (value == "selectBuildingPlan") {
            state.dialogManager.openDialog(
              <UploadFloorPlansDialog
                onChange={() => {}}
                page={new Page()}
                fromFloorCreation={{
                  tag: tag,
                  name: name,
                  cabinetId: cabinetId,
                  background: {
                    page: fromSubmittals?.background.page,
                    pageNumber: fromSubmittals?.background.pageNumber || 0,
                  },
                }}
              />,
            );
          }
        }}
      >
        {options}
      </select>
    );
  }

  // Called only after PDF confirmation, inside the import's undo transaction.
  function createImportedFloor(selection: PdfImportPageSelection): C1_Floor {
    if (state.floors.asArray().some(floor => floor.tag.toLocaleUpperCase() === selection.floorTag.toLocaleUpperCase())) {
      throw new Error(itx("c1.pdfImport.duplicateFloorTag", "Etagen-Tags müssen eindeutig sein."));
    }
    let selectedCabinetId = cabinetId;
    if (selectedCabinetId === "create") {
      const cabinet = new Cabinet({
        name: state.cabinets.size ? state.translate("components.Cabinet", "Schaltschrank") + " " + selection.floorName : "",
      }, state.cabinets);
      cabinet.tag = state.nameAndTagManager.generateTag(cabinet);
      state.cabinets.add(cabinet);
      selectedCabinetId = cabinet.id;
    }
    if (!state.cabinets.asArray().some(cabinet => cabinet.id === selectedCabinetId)) {
      throw new Error(itx("floorDialog.selectCabinet", "Bitte einen Schaltschrank auswählen."));
    }
    const floor = new Floor({ name: selection.floorName, tag: selection.floorTag, cabinetId: selectedCabinetId }, parent);
    const levels = state.floors.asArray().flatMap(floor => floor.c1 ? [floor.c1.level] : []);
    floor.c1 = new C1_Floor({ level: Math.max(-1, ...levels) + 1, ownCabinet: cabinetId === "create" }, floor);
    parent.add(floor);
    return floor.c1;
  }

  return (
    <BaseDialog
      title={
        edit
          ? state.translate("floorDialog.editFloor", "Etage bearbeiten")
          : state.translate("floorDialog.createFloor", "Neue Etage anlegen")
      }
      enterAsOk={true}
      hideXButton={true}
      okCaption={itx("floorDialog.create")}
      okDisabled={importBusy}
      onOk={() => {
        if (!cabinetId || cabinetId == "start") {
          setCabinetIsMissing(true);
          return false;
        } else {
          setCabinetIsMissing(false);
        }
        return createNewFloor();
      }}
    >
      <FieldSet>
        <Field label={state.translate("tag", "Tag")}>
          <input
            id="FloorDialog_tag"
            className="input"
            ref={tagInputRef}
            maxLength={state.maxCharLimit.tag}
            defaultValue={tag}
            onChange={(ev) => {
              const v = ev.target.value.toLocaleUpperCase().trim();
              ev.target.value = v;
              setTag(v);
              tagIsSetManual.current = true;
            }}
          ></input>
        </Field>

        <Field label={state.translate("name", "Name")}>
          <div style={{ position: "relative", height: "30px" }}>
            <select
              className="input"
              style={{ position: "absolute", width: "100%", left: "0px", top: "0px" }}
              onChange={(ev) => {
                if (nameInputRef.current) {
                  nameInputRef.current.value = ev.currentTarget.value;
                  updateName(ev.currentTarget.value);
                }
              }}
            >
              {options}
            </select>
            <input
              ref={nameInputRef}
              id="FloorDialog_name"
              className="input"
              style={{
                position: "absolute",
                width: "calc(100% - 20px)",
                left: "0px",
                top: "0px",
                zIndex: 10,
                borderTopRightRadius: "0px",
                borderBottomRightRadius: "0px",
              }}
              maxLength={state.maxCharLimit.text}
              defaultValue={name}
              onInput={(ev) => {
                updateName(ev.currentTarget.value);
              }}
              onBlur={(ev) => {
                console.log("onBlur");
              }}
            ></input>
          </div>
        </Field>

        <Field label={state.translate("components.Cabinet", "Schaltschrank")}>
          <CabinetSelection
            style={{ backgroundColor: cabinetIsMissing ? "red" : undefined, width: undefined }}
            value={cabinetId}
            onChange={(id) => {
              setCabinetId(id);
              if (id) {
                setCabinetIsMissing(false);
              }
            }}
            floorRoom={obj}
          ></CabinetSelection>
        </Field>
        <Field label={itx("floorDialog.backgroundPDF", "background pdf")}>
          <div style={{ display: "flex", flexDirection: "row", alignContent: "center" }}>
            {/* {fromSubmittals && <ShowBackground />}
            here dropdown  */}
            {state.projectMetadata.isC1Project && !edit ? (
              <C1_PdfImportButton
                floorImport={{ name, tag, create: createImportedFloor }}
                onBusyChange={setImportBusy}
                beforeImport={() => {
                  const valid = !!cabinetId && cabinetId !== "start";
                  setCabinetIsMissing(!valid);
                  return valid;
                }}
                onImported={floor => {
                  state.dialogManager.closeDialog(true);
                  floor?.owner?.treeViewModel.handleOpen();
                }}
              />
            ) : <FloorPlansSelection />}
          </div>
        </Field>
      </FieldSet>
    </BaseDialog>
  );
}
